import classNames from 'classnames'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { Input } from '..'
import { MAX_TEAM_NAME_LENGTH } from '../../screens/Organization/Create/constants'
import { InputProps } from '../Input'
import { Form } from './Form'
import { useIntl } from 'react-intl'

type TeamNameInputProps<T extends FieldValues> = Pick<
  UseControllerProps<T>,
  'control' | 'rules' | 'name'
> & {
  className?: string
  logoURL?: string
} & InputProps

export function TeamNameInput<T extends FieldValues>({
  name,
  logoURL,
  control,
  className,
  rules,
  ...props
}: TeamNameInputProps<T>) {
  const intl = useIntl()
  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      ...rules,
      required: true,
      maxLength: {
        value: MAX_TEAM_NAME_LENGTH,
        message: intl.formatMessage(
          {
            id: 'boarding.form.teamName.maxLength',
            defaultMessage:
              'Team name should be less than {MAX_TEAM_NAME_LENGTH} characters',
          },
          {
            MAX_TEAM_NAME_LENGTH,
          }
        ),
      },
    },
  })
  const renderLogo = logoURL
    ? () => (
        <img
          src={logoURL}
          className={classNames(
            'avatar height-full width-full',
            'background-cover background-center background-no-repeat',
            'shade-100 radius-round',
            'margin-left-12'
          )}
          style={{ height: 24, width: 24 }}
          alt={intl.formatMessage({
            id: 'boarding.form.teamName.logoAlt',
            defaultMessage: 'logo',
          })}
        />
      )
    : undefined

  return (
    <>
      <Input
        type="text"
        autoFocus
        className={classNames('margin-vertical-8', className)}
        placeholder="Wonka Industries"
        autoComplete="organization"
        renderLeft={renderLogo}
        data-test-id={`${name}Field`}
        error={fieldState.invalid}
        {...field}
        {...props}
      />
      <Form.Error message={fieldState?.error?.message} />
    </>
  )
}
